import React, { useState } from 'react';
import PricingCard from './PricingCard';
import '../subscription-plan.scss';
import { useNavigate } from 'react-router-dom';
import { axiosInstanceV2 } from '../../../services/axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { usePayment } from '../../../hooks/usePayment';

const PricingSection = ({ billing, setBilling, plans, hideFreePlan }) => {
	const navigate = useNavigate();
	const { initiatePayment } = usePayment();
	const [selectedPlan, setSelectedPlan] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [confirmModal, setConfirmModal] = useState(null);
	const [loading, setLoading] = useState(false);
	const subscription = useSelector((state) => state.userInfo.userInfo?.data?.subscription);
	const currentRazorpayPlanId = subscription?.plan.razorpay_plan_id ?? null;
	const currentPlanKey = subscription?.plan.key ?? null;
	const isExistingUser = !!subscription;

	const handleOpenFreeModal = (plan) => {
		setSelectedPlan(plan);
		setShowModal(true);
	};

	const activateFreePlan = async () => {
		try {
			setLoading(true);
			const authToken = localStorage.getItem('authToken');

			const response = await axiosInstanceV2.get('/subscription/start-free', {
				headers: { Authorization: `Bearer ${authToken}` },
			});

			if (response?.data?.success) {
				toast.success('Your Free Plan is Active');
				setShowModal(false);
				navigate('/organizationDetail');
				return;
			}

			if (response?.data?.message === 'Subscription already exists') {
				toast.info('Free plan already active.');
				setShowModal(false);
				setTimeout(() => navigate('/organizationDetail'), 300);
				return;
			}

			toast.error(response?.data?.message || 'Failed to activate free plan');
		} catch (error) {
			const errorMessage = error?.response?.data?.message;
			if (errorMessage === 'Subscription already exists') {
				toast.info('Free plan already active.');
				setShowModal(false);
				setTimeout(() => navigate('/organizationDetail'), 300);
				return;
			}
			toast.error(errorMessage || 'Failed to activate free plan');
		} finally {
			setLoading(false);
		}
	};

	const handleSelectPlan = (plan, type) => {
		if (!isExistingUser) {
			navigate('/checkout', { state: { plan, billing } });
			return;
		}
		setConfirmModal({ plan, type });
	};

	const handleConfirmUpgrade = async () => {
		if (!confirmModal) return;
		const { plan } = confirmModal;

		const activeBilling = plan.billing?.find((b) => b.period === billing);
		if (!activeBilling) {
			toast.error('Plan not available for selected billing period');
			return;
		}

		try {
			setLoading(true);
			const authToken = localStorage.getItem('authToken');

			const response = await axiosInstanceV2.post(
				'/subscription/upgrade',
				{
					razorpay_plan_id: activeBilling.razorpay_plan_id,
					billing_period: billing,
					is_recurring: true,
				},
				{ headers: { Authorization: `Bearer ${authToken}` } }
			);

			const result = response.data.data;

			if (result.type === 'upgrade') {
				setConfirmModal(null);

				initiatePayment({
					billingDetails: result.billing_details ?? {},
					plan: {
						razorpay_plan_id: activeBilling.razorpay_plan_id,
						title: plan.name,
						billingPeriod: billing,
					},
					paymentType: 'recurring',
					paymentMethod: 'card',
				});
			}

			if (result.type === 'downgrade_scheduled') {
				toast.success(
					`Downgrade to ${plan.name} scheduled for ${new Date(result.effective_date).toLocaleDateString()}`
				);
				setConfirmModal(null);
			}

		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Failed to update subscription');
		} finally {
			setLoading(false);
		}
	};

	const filteredPlans = hideFreePlan
		? plans?.filter((plan) => plan?.name?.toLowerCase() !== 'free')
		: plans;

	return (
		<>
			<div className="pricing-wrapper">
				<div className="billing-toggle">
					<button
						className={billing === 'monthly' ? 'active' : ''}
						onClick={() => setBilling('monthly')}
					>
						Monthly
					</button>
					<button
						className={billing === 'yearly' ? 'active' : ''}
						onClick={() => setBilling('yearly')}
					>
						Yearly
					</button>
				</div>

				<div className="pricing-grid">
					{filteredPlans?.map((plan, i) => (
						<PricingCard
							key={i}
							plan={plan}
							billing={billing}
							currentRazorpayPlanId={isExistingUser ? currentRazorpayPlanId : null}
							currentPlanKey={isExistingUser ? currentPlanKey : null}
							onOpenFreeModal={handleOpenFreeModal}
							onSelectPlan={handleSelectPlan}
						/>
					))}
				</div>
			</div>

			{showModal && (
				<div
					className="modal fade show"
					style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
				>
					<div className="modal-dialog modal-dialog-top">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Free Plan Activation</h5>
								<button type="button" className="btn-close" onClick={() => setShowModal(false)} />
							</div>
							<div className="modal-body">
								<p className="fw-semibold mb-2">Your Free Plan is Ready 🎉</p>
								<p className="text-muted mb-0">
									You can now continue with the free plan and complete your organization setup.
								</p>
							</div>
							<div className="modal-footer">
								<div className="d-flex justify-content-end align-items-center gap-3 w-100">
									<button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
										Cancel
									</button>
									<button
										type="button"
										className="btn-primary-modal"
										onClick={activateFreePlan}
										disabled={loading}
									>
										{loading ? 'Loading...' : 'Continue Setup'}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{confirmModal && (
				<div
					className="modal fade show"
					style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}
				>
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									{confirmModal.type === 'upgrade' ? 'Confirm Upgrade' : 'Confirm Downgrade'}
								</h5>
								<button type="button" className="btn-close" onClick={() => setConfirmModal(null)} />
							</div>
							<div className="modal-body">
								{confirmModal.type === 'upgrade' ? (
									<p>
										You're upgrading to <strong>{confirmModal.plan.name}</strong>.
										Your current plan will be cancelled and the new plan will
										activate immediately after payment.
									</p>
								) : (
									<p>
										You're downgrading to <strong>{confirmModal.plan.name}</strong>.
										This takes effect at the end of your current billing cycle —
										you'll keep current plan benefits until then.
									</p>
								)}
							</div>
							<div className="modal-footer">
								<div className="d-flex justify-content-end align-items-center gap-3 w-100">
									<button type="button" className="btn-cancel" onClick={() => setConfirmModal(null)}>
										Cancel
									</button>
									<button
										type="button"
										className="btn-primary-modal"
										onClick={handleConfirmUpgrade}
										disabled={loading}
									>
										{loading
											? 'Processing...'
											: confirmModal.type === 'upgrade' ? 'Proceed to Payment' : 'Confirm Downgrade'
										}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PricingSection;